var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { IBoundingBox, IMatrix4, ITransformedBoundingBox, IVector3 } from '../api.js';
import { RENDER_LAYER_MASK_DEFAULT, RenderLayerMask } from '../babylon/RenderLayer.js';
import { Vector3ConstantReference } from './material/PropertyWrappers.js';
import { Disposable } from '../utils/Disposable.js';
import { Observable } from '../Observable.js';
/**
 * Class for handling drawing vertex buffers using line primitives
 * Line primitives are not considered normal SceneObjects and can't be interacted with with raycasting or any other means.
 */
let SceneLines = class SceneLines {
    constructor(mesh, _viewer) {
        this.mesh = mesh;
        this._viewer = _viewer;
        this.disposed_observable = new Observable();
        this._render_layer_mask = RENDER_LAYER_MASK_DEFAULT;
        this._local_bounds = IBoundingBox.Invalid();
        const on_transform_update = () => {
            this._viewer.mark_scene_dirty();
            this._viewer.mark_world_bounds_dirty();
        };
        this._position_wrapper = new Vector3ConstantReference(mesh, 'position', on_transform_update);
        this._rotation_wrapper = new Vector3ConstantReference(mesh, 'rotation', on_transform_update);
        this._scale_wrapper = new Vector3ConstantReference(mesh, 'scaling', on_transform_update);
    }
    recalculate_local_bounds(description) {
        this._local_bounds.min = new IVector3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY);
        this._local_bounds.max = new IVector3(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY);
        for (let i = 0; i < description.positions.length; i += 3) {
            const x = description.positions[i + 0];
            const y = description.positions[i + 1];
            const z = description.positions[i + 2];
            this._local_bounds.min.x = Math.min(this._local_bounds.min.x, x);
            this._local_bounds.min.y = Math.min(this._local_bounds.min.y, y);
            this._local_bounds.min.z = Math.min(this._local_bounds.min.z, z);
            this._local_bounds.max.x = Math.max(this._local_bounds.max.x, x);
            this._local_bounds.max.y = Math.max(this._local_bounds.max.y, y);
            this._local_bounds.max.z = Math.max(this._local_bounds.max.z, z);
        }
    }
    get_world_bounds() {
        return new ITransformedBoundingBox(this._local_bounds, this.mesh.getWorldMatrix().toIMatrix4ToRef(new IMatrix4()));
    }
    get position() {
        return this._position_wrapper;
    }
    set position(value) {
        this._position_wrapper.x = value.x;
        this._position_wrapper.y = value.y;
        this._position_wrapper.z = value.z;
        this._viewer.mark_scene_dirty();
        this._viewer.mark_world_bounds_dirty();
    }
    get rotation() {
        return this._rotation_wrapper;
    }
    set rotation(value) {
        this._rotation_wrapper.x = value.x;
        this._rotation_wrapper.y = value.y;
        this._rotation_wrapper.z = value.z;
        this._viewer.mark_scene_dirty();
        this._viewer.mark_world_bounds_dirty();
    }
    get scale() {
        return this._scale_wrapper;
    }
    set scale(value) {
        this._scale_wrapper.x = value.x;
        this._scale_wrapper.y = value.y;
        this._scale_wrapper.z = value.z;
        this._viewer.mark_scene_dirty();
        this._viewer.mark_world_bounds_dirty();
    }
    get visible() {
        // Visibility is a number from 0-1 but for LinesMesh any > 0 value is fully visible
        return this.mesh.visibility > 0;
    }
    set visible(value) {
        // Visibility is a number from 0-1 but for LinesMesh any > 0 value is fully visible
        if (value === this.visible)
            return;
        this.mesh.visibility = value ? 1 : 0;
        this._viewer.mark_scene_dirty();
        this._viewer.mark_world_bounds_dirty();
    }
    get render_layer_mask() {
        return this._render_layer_mask;
    }
    set render_layer_mask(value) {
        if (this._render_layer_mask === value)
            return;
        this._render_layer_mask = value;
        this.mesh.layerMask = this._render_layer_mask.bitmap;
        this._viewer.mark_scene_dirty();
        this._viewer.mark_world_bounds_dirty();
    }
    add_render_layer(layer) {
        this.render_layer_mask = RenderLayerMask.add_layer(this._render_layer_mask, layer);
    }
    remove_render_layer(layer) {
        this.render_layer_mask = RenderLayerMask.remove_layer(this._render_layer_mask, layer);
    }
    dispose() {
        this._viewer.mark_scene_dirty();
        this.disposed_observable.notify(this);
        // Disposes both mesh and material by default
        this.mesh.dispose();
    }
};
SceneLines = __decorate([
    Disposable
], SceneLines);
export { SceneLines };
//# sourceMappingURL=SceneLines.js.map